/**
 * Formato de cifras y fechas para las tablas.
 *
 * El dinero llega de la API como cadena decimal —`"1234.56"`— y **así se
 * queda hasta el último momento**: convertirlo a `number` para pintarlo es
 * la puerta de entrada a los errores de coma flotante que la ADR-001
 * prohíbe en el dominio. Aquí sólo se le pone formato, nunca se opera.
 */

const MONEY = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const QUANTITY = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

/** Importe con separador de miles y dos decimales, sin operar con él. */
export function money(value: string | null | undefined, currency = 'EUR'): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return `${MONEY.format(parsed)} ${currency === 'EUR' ? '€' : currency}`;
}

/** Cantidad de almacén: hasta cuatro decimales, sin ceros de relleno. */
export function quantity(value: string | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return unit ? `${QUANTITY.format(parsed)} ${unit}` : QUANTITY.format(parsed);
}

/** Fecha `YYYY-MM-DD` tal cual: ya viene del servidor sin zona horaria. */
export function day(value: string | null | undefined): string {
  return value ?? '—';
}

/** Marca de tiempo recortada al día, para no ensuciar la tabla. */
export function dayOf(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '—';
}

/**
 * Fecha con hora, para leer.
 *
 * `day()` deja pasar el valor tal cual, que sirve para una fecha suelta pero
 * no para una marca de tiempo: enseñaba el texto completo del objeto de fecha,
 * zona horaria incluida y en inglés.
 */
export function dateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const dos = (n: number): string => String(n).padStart(2, '0');
  return `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()} ${dos(
    d.getHours(),
  )}:${dos(d.getMinutes())}`;
}
